<template>
  <div>
    <!-- Filters -->
    <transition name="fade" mode="out-in">
      <b-card key="filters" v-if="showFilters" no-body class="mb-2">
        <b-card-header class="d-flex justify-content-between pl-1 pt-1">
          <div class="d-flex align-items-center">
            <h5 class="mb-0">
              {{ $t('Filters') }}
            </h5>
            <b-badge v-if="hasActiveFilters" variant="primary" class="ml-2">
              {{ $t('Active') }}
            </b-badge>
          </div>
          <div class="d-flex align-items-center">
            <b-button
                v-if="hasActiveFilters"
                variant="outline-secondary"
                size="sm"
                @click="clearFilters"
                class="mr-2"
                v-b-tooltip.hover
                :title="$t('Clear filters')">
              <feather-icon icon="Trash2Icon" size="14"/>
            </b-button>
            <b-avatar @click="showFilters = false" v-b-tooltip.hover :title="$t('Close Filters')" class="mr-1"
                      button variant="danger" size="30px">
              <feather-icon icon="XIcon"/>
            </b-avatar>
          </div>
        </b-card-header>
        <b-card-body>
          <!-- Primeira linha de filtros -->
          <b-row class="mb-1">
            <!-- Field: Owner -->
            <b-col cols="12" md="6">
              <h5 class="font-weight-bold">{{ $t('Owner') }}</h5>
              <vue-autosuggest v-model="searchOwner" :suggestions="filteredOptions" :input-props="inputProps"
                               @selected="selectHandler" @input="onInputChange">
                <template slot-scope="{suggestion}">
                  <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
                  <div v-show="suggestion.item.ownerName">
                    <span class="text-muted">{{ $t('Belongs to: ') }}</span>
                    <span class="font-weight-bold"> {{ suggestion.item.ownerName }} </span>
                  </div>
                </template>
              </vue-autosuggest>
            </b-col>

            <!-- Field: Search -->
            <b-col cols="12" md="6">
              <h5 class="font-weight-bold">{{ $t('Search') }}</h5>
              <b-form-input
                  v-model="searchQuery"
                  :placeholder="$t('Search ...')"
              />
            </b-col>
          </b-row>

          <!-- Segunda linha de filtros -->
          <b-row class="mb-1">
            <!-- Field: Asset Type -->
            <b-col cols="12" md="6" lg="3">
              <h5 class="font-weight-bold">{{ $t('Asset Type') }}</h5>
              <v-select v-model="assetTypeId" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="assetsTypeOptions" :reduce="val => val.value" :clearable="true" 
                        input-id="asset-type" :placeholder="$t('All')"/>
            </b-col>

            <!-- Field: Manufacturer -->
            <b-col cols="12" md="6" lg="3">
              <h5 class="font-weight-bold">{{ $t('Manufacturer') }}</h5>
              <v-select v-model="assetBrandId" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="manufacturerOptions" :reduce="val => val.value" :clearable="true"
                        input-id="manufacturer" :placeholder="$t('All')" @input="onManufacturerChange"/>
            </b-col>

            <!-- Field: Model -->
            <b-col cols="12" md="6" lg="3">
              <h5 class="font-weight-bold">{{ $t('Model') }}</h5>
              <v-select v-model="assetModelId" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="modelOptions" :reduce="val => val.value" :clearable="true"
                        input-id="model" :placeholder="$t('All')" :disabled="!assetBrandId"/>
            </b-col>

            <!-- Field: Group -->
            <b-col cols="12" md="6" lg="3">
              <h5 class="font-weight-bold">{{ $t('Group') }}</h5>
              <v-select v-model="assetGroupId" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        :options="groupOptions" :reduce="val => val.value" :clearable="true"
                        input-id="group" :placeholder="$t('All')"/>
            </b-col>
          </b-row>

          <!-- Terceira linha de filtros - Datas e Horários -->
          <b-row class="mb-2">
            <!-- Field: Data Início -->
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="$t('Start Date')" label-for="start-date">
                <flat-pickr
                    id="start-date"
                    v-model="startDate"
                    class="form-control"
                    :config="dateConfig"
                    :placeholder="$t('DD/MM/YYYY')"
                    @on-change="onDateChange"
                />
                <small class="text-muted d-block mt-1">
                  <feather-icon icon="InfoIcon" size="12" class="mr-1"/>
                  {{ $t('Search period: up to 7 days') }} | {{ $t('Export period: up to 31 days') }}
                </small>
              </b-form-group>
            </b-col>

            <!-- Field: Data Fim -->
            <b-col cols="12" md="6" lg="3">
              <b-form-group :label="$t('End Date')" label-for="end-date">
                <flat-pickr
                    id="end-date"
                    v-model="endDate"
                    class="form-control"
                    :config="dateConfig"
                    :placeholder="$t('DD/MM/YYYY')"
                    @on-change="onDateChange"
                />
                <div v-if="selectedDateRangeDays > 0" class="mt-1">
                  <b-badge 
                      :variant="getDateRangeBadgeVariant()" 
                      class="d-inline-flex align-items-center">
                    <feather-icon icon="CalendarIcon" size="12" class="mr-1"/>
                    <small>{{ selectedDateRangeDays }} {{ selectedDateRangeDays === 1 ? $t('day') : $t('days') }}</small>
                  </b-badge>
                  <b-badge 
                      v-if="selectedDateRangeDays > 7 && selectedDateRangeDays <= 31" 
                      variant="warning" 
                      class="ml-1 d-inline-flex align-items-center">
                    <feather-icon icon="AlertTriangleIcon" size="12" class="mr-1"/>
                    <small>{{ $t('Export only') }}</small>
                  </b-badge>
                </div>
              </b-form-group>
            </b-col>

            <!-- Field: Horário Início -->
            <b-col cols="12" md="4" lg="2">
              <b-form-group :label="$t('Shift Start')" label-for="shift-start">
                <flat-pickr
                    id="shift-start"
                    v-model="shiftStartTime"
                    class="form-control"
                    :config="timeConfig"
                    :placeholder="$t('HH:mm')"
                />
              </b-form-group>
            </b-col>

            <!-- Field: Horário Fim -->
            <b-col cols="12" md="4" lg="2">
              <b-form-group :label="$t('Shift End')" label-for="shift-end">
                <flat-pickr
                    id="shift-end"
                    v-model="shiftEndTime"
                    class="form-control"
                    :config="timeConfig"
                    :placeholder="$t('HH:mm')"
                />
              </b-form-group>
            </b-col>

            <!-- Field: Paradas -->
            <b-col cols="12" md="4" lg="2">
              <b-form-group :label="$t('Breaks')" label-for="breaks">
                <flat-pickr
                    id="breaks"
                    v-model="breaks"
                    class="form-control"
                    :config="timeConfig"
                    :placeholder="$t('HH:mm')"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" class="d-flex justify-content-end align-items-center flex-wrap">
              <!-- Botões de ação -->
              <div class="d-flex align-items-center">
                <b-button
                    variant="primary"
                    @click="handleSearch"
                    :disabled="isLoading || isSearchDisabled"
                    v-b-tooltip.hover
                    :title="isSearchDisabled ? $t('Search period cannot exceed 7 days') : $t('Search')"
                    class="mr-2">
                  <b-spinner v-if="isLoading" small class="mr-1"/>
                  <feather-icon v-else icon="SearchIcon" class="mr-1"/>
                  {{ $t('Search') }}
                </b-button>
                
                <b-button
                    variant="success"
                    @click="handleExportExcel"
                    :disabled="isLoading || isExporting"
                    v-b-tooltip.hover
                    :title="$t('Export allows up to 31 days period')">
                  <b-spinner v-if="isExporting" small class="mr-1"/>
                  <feather-icon v-else icon="DownloadIcon" size="14" class="mr-1"/>
                  {{ $t('Export Excel') }}
                </b-button>
              </div>
            </b-col>
          </b-row>

          <b-row v-if="dateRangeWarning" class="mt-2">
            <b-col cols="12">
              <b-alert show variant="warning" class="mb-0">
                <feather-icon icon="AlertTriangleIcon" class="mr-1"/>
                {{ dateRangeWarning }}
              </b-alert>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </transition>

    <!-- Table Container Card -->
    <b-card no-body>
      <div v-show="hasSearched || isLoading" class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col cols="12" md="6" class="mb-1 mb-md-0">
            <div class="d-flex align-items-center justify-content-start mb-1">
              <label>{{ $t('Show') }}</label>
              <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
                        :clearable="false" class="per-page-selector d-inline-block ml-50 mr-1"/>
              <label>{{ $t('entries') }}</label>
            </div>
            <!-- Legenda de Eficiência -->
            <div class="efficiency-legend d-flex align-items-center flex-wrap">
              <small class="text-muted mr-1">{{ $t('Efficiency') }}:</small>
              <b-badge variant="success" class="mr-1">
                <small>≥90%</small>
              </b-badge>
              <b-badge variant="info" class="mr-1">
                <small>70-89%</small>
              </b-badge>
              <b-badge variant="warning" class="mr-1">
                <small>50-69%</small>
              </b-badge>
              <b-badge variant="danger" class="mr-1">
                <small>1-49%</small>
              </b-badge>
              <b-badge variant="secondary">
                <small>0%</small>
              </b-badge>
            </div>
          </b-col>

          <!-- Actions -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <span v-show="!showFilters" class="float-right">
                <b-button
                    class="btn-icon position-relative"
                    @click="showFilters = true"
                    :variant="hasActiveFilters ? 'success' : 'primary'"
                    v-b-tooltip.hover
                    :title="hasActiveFilters ? $t('Open Filters (Active)') : $t('Open Filters')">
                  <feather-icon icon="FilterIcon"/>
                  <b-badge
                      v-if="hasActiveFilters"
                      variant="warning"
                      pill
                      class="position-absolute badge-notification">
                    !
                  </b-badge>
                </b-button>
              </span>

              <b-button
                  v-if="hasActiveFilters"
                  class="ml-1"
                  variant="outline-secondary"
                  style="min-width: 150px; white-space: nowrap;"
                  @click="handleClearFilters"
                  v-b-tooltip.hover
                  :title="$t('Clear Filters')">
                <feather-icon icon="XIcon" size="14"/>
                {{ $t('Clear Filters') }}
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Empty State Message (antes da busca) -->
      <div v-if="!hasSearched && !isLoading" class="empty-state-container">
        <div class="empty-state-content">
          <div class="empty-state-icon">
            <feather-icon icon="SearchIcon" size="64"/>
          </div>
          <h4 class="empty-state-title">{{ $t('Ready to search') }}</h4>
          <p class="empty-state-description">
            {{ $t('Use the filters above to configure your search criteria and click the Search button to view the productivity report.') }}
          </p>
          <div class="empty-state-hint">
            <feather-icon icon="InfoIcon" size="16" class="mr-1"/>
            <small>{{ $t('The filters are pre-configured with default values. You can adjust them as needed.') }}</small>
          </div>
        </div>
      </div>

      <!-- Table with horizontal scroll -->
      <div v-show="hasSearched || isLoading" class="table-responsive" style="overflow-x: auto;">
        <b-table
            ref="refOperationalTimeTable"
            :items="operationalData"
            responsive
            :fields="tableColumns"
            primary-key="assetId"
            show-empty
            :empty-text="$t('No data found for the selected filters. Try adjusting your search criteria.')"
            :busy="isLoading"
            class="position-relative operational-time-table"
            sticky-header="600px">

          
          <template #cell(plate)="data">
            <div class="plate-identifier-cell">
              <template v-if="data.item.plate || data.item.chassis || data.item.nickname">
                <div v-if="data.item.plate" class="mb-1">
                  <small class="text-muted">{{ $t('Plate') }}:</small>
                  <span class="font-weight-bold text-uppercase ml-1">{{ data.item.plate }}</span>
                </div>
                <div v-if="data.item.chassis" class="mb-1">
                  <small class="text-muted">{{ $t('Chassis') }}:</small>
                  <span class="font-weight-bold text-uppercase ml-1">{{ data.item.chassis }}</span>
                </div>
                <div v-if="data.item.nickname">
                  <small class="text-muted">{{ $t('Nickname') }}:</small>
                  <span class="font-weight-bold ml-1">{{ data.item.nickname }}</span>
                </div>
                <div v-if="data.item.additional1 && data.item.additional1.trim()">
                  <small class="text-muted">{{ $t('Additional 1') }}:</small>
                  <span class="font-weight-bold ml-1">{{ data.item.additional1 }}</span>
                </div>
                <div v-if="data.item.additional2 && data.item.additional2.trim()">
                  <small class="text-muted">{{ $t('Additional 2') }}:</small>
                  <span class="font-weight-bold ml-1">{{ data.item.additional2 }}</span>
                </div>
                <div v-if="data.item.additional3 && data.item.additional3.trim()">
                  <small class="text-muted">{{ $t('Additional 3') }}:</small>
                  <span class="font-weight-bold ml-1">{{ data.item.additional3 }}</span>
                </div>
                <div v-if="data.item.additional4 && data.item.additional4.trim()">
                  <small class="text-muted">{{ $t('Additional 4') }}:</small>
                  <span class="font-weight-bold ml-1">{{ data.item.additional4 }}</span>
                </div>
              </template>
              <template v-else>
                <span class="text-muted">{{ $t('No identifier') }}</span>
              </template>
            </div>
          </template>

          <!-- Dynamic Date Columns (somente colunas de data) -->
          <template v-for="col in tableColumns.filter(c => c.key.startsWith('date_'))" v-slot:[`cell(${col.key})`]="data">
            <div :key="col.key">
              <template v-if="getOperationalTimeForDate(data.item, col.label)">
                <div class="operational-time-cell">
                  <div class="mb-50">
                    <small class="text-muted">{{ $t('Scheduled') }}:</small>
                    <strong class="ml-50">{{ formatTime(getOperationalTimeForDate(data.item, col.label).totalScheduledTime) }}</strong>
                  </div>
                  <div class="mb-50">
                    <small class="text-muted">{{ $t('Actual') }}:</small>
                    <strong class="ml-50">{{ formatTime(getOperationalTimeForDate(data.item, col.label).actualRunTime) }}</strong>
                  </div>
                  <div class="mb-50">
                    <small class="text-muted">{{ $t('Downtime') }}:</small>
                    <strong class="ml-50">{{ formatTime(getOperationalTimeForDate(data.item, col.label).downtime) }}</strong>
                  </div>
                  <div>
                    <small class="text-muted">{{ $t('Efficiency') }}:</small>
                    <b-badge :variant="getEfficiencyVariant(getOperationalTimeForDate(data.item, col.label).efficiencyPercentage)" class="ml-50">
                      {{ formatPercentage(getOperationalTimeForDate(data.item, col.label).efficiencyPercentage) }}
                    </b-badge>
                  </div>
                </div>
              </template>
            </div>
          </template>

          <!-- Column: Mean Actual Run Time -->
          <template #cell(meanActualRunTime)="data">
            <div class="text-center">
              <strong>{{ formatTime(data.item.meanActualRunTime) }}</strong>
            </div>
          </template>

          <!-- Column: Mean Efficiency Percentage -->
          <template #cell(meanEfficiencyPercentage)="data">
            <div class="text-center">
              <b-badge :variant="getEfficiencyVariant(data.item.meanEfficiencyPercentage)" class="badge-glow">
                {{ formatPercentage(data.item.meanEfficiencyPercentage) }}
              </b-badge>
            </div>
          </template>

          <!-- Loading State -->
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle mr-1"/>
              <strong>{{ $t('Loading...') }}</strong>
            </div>
          </template>

        </b-table>
      </div>

      <div v-show="hasSearched" class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">{{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{
                dataMeta.to
              }} {{ $t('of') }} {{ dataMeta.of }} {{ $t('entries') }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination v-model="currentPage" :total-rows="totalItems" :per-page="perPage" first-number last-number
                          class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item"
                          @change="handlePageChange">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18"/>
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18"/>
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BAvatar,
  BBadge,
  BPagination,
  BCardBody,
  BCardHeader,
  BSpinner,
  BFormGroup,
  BAlert,
  VBTooltip
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { onUnmounted, ref } from '@vue/composition-api'
import store from '@/store'
import useOperationalTimeFleet from './useOperationalTimeFleet'
import operationalTimeStoreModule from './operationalTimeStoreModule'
import assetStoreModule from '../asset/assetStoreModule'
import managementStoreModule from '../management/managementStoreModule'
import { VueAutosuggest } from 'vue-autosuggest'
import flatPickr from 'vue-flatpickr-component'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'

export default {
  components: {
    BAlert,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BAvatar,
    BBadge,
    BPagination,
    BCardBody,
    BCardHeader,
    vSelect,
    BSpinner,
    VueAutosuggest,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      dateRangeWarning: '',
      filteredOptions: [],
      isExporting: false,
      inputProps: {
        id: 'autosuggest__input',
        class: 'form-control',
        placeholder: this.$t('Search owner ...'),
      },
      selected: null,
      dateConfig: {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd/m/Y',
        locale: getFlatpickrLocale(),
        allowInput: true,
      },
      timeConfig: {
        enableTime: true,
        noCalendar: true,
        dateFormat: 'H:i',
        time_24hr: true,
        locale: getFlatpickrLocale(),
        allowInput: true,
      },
    }
  },
  methods: {
    onDateChange() {
      // Atualizar feedback visual quando as datas mudarem
      this.$forceUpdate()
    },

    getDateRangeBadgeVariant() {
      const days = this.selectedDateRangeDays
      if (days <= 7) return 'success'
      if (days <= 31) return 'warning'
      return 'danger'
    },

    handleSearch() {
      const validation = this.validateDateRangeForSearch()
      
      if (!validation.valid) {
        this.dateRangeWarning = validation.message
        this.$swal({
          title: this.$t('Attention'),
          text: validation.message,
          icon: 'warning',
          confirmButtonText: this.$t('OK'),
        })
        return
      }
      
      this.dateRangeWarning = ''
      this.currentPage = 1
      
      this.fetchOperationalTime()
        .then(() => {
          if (this.operationalData.length > 0) {
          }
        })
        .catch(error => {
          this.$swal({
            title: this.$t('Error'),
            text: error.message || this.$t('An error occurred while fetching data'),
            icon: 'error',
            confirmButtonText: this.$t('OK'),
          })
        })
    },

    handleClearFilters() {
      this.clearFilters()
      this.dateRangeWarning = ''
      this.filteredOptions = []
      this.selected = null
    },

    handlePageChange(page) {
      this.currentPage = page
      this.fetchOperationalTime()
    },

    selectHandler(option) {
      this.selected = option.item
      this.filteredOptions = []
      this.updateSearchOwner(option.item.name)
      this.updateOwnerId(option.item.id)
    },

    onInputChange(text) {
      this.updateSearchOwner(text || '')

      if (text === '' || text === undefined || text.length < 3) {
        this.updateOwnerId('')
        this.filteredOptions = []
        return
      }

      store.dispatch('app-asset/fetchCustomers', { q: text })
        .then(response => {
          this.filteredOptions = [{
            data: response.data.data,
          }]
        })
        .catch(() => {})
    },

    onManufacturerChange(manufacturerId) {
      this.assetModelId = ''
      this.modelOptions = [{ label: this.$t('All'), value: '' }]
      
      if (!manufacturerId) return

      store.dispatch('app-asset/fetchAssetsModel', { id: manufacturerId, page: 1 })
        .then(response => {
          response.data.data.forEach(model => {
            this.modelOptions.push({
              label: model.name,
              value: model.id
            })
          })
        })
        .catch(() => {})
    },

    async handleExportExcel() {
      // Validar datas antes de exportar (31 dias)
      const validation = this.validateDateRangeForExport()
      
      if (!validation.valid) {
        this.dateRangeWarning = validation.message
        this.$swal({
          title: this.$t('Attention'),
          text: validation.message,
          icon: 'warning',
          confirmButtonText: this.$t('OK'),
        })
        return
      }

      // Verificar se o período está dentro do limite de busca (7 dias)
      const searchValidation = this.validateDateRangeForSearch()
      if (!searchValidation.valid) {
        // Informar que a exportação pode ser feita mesmo sem busca prévia
        const days = this.selectedDateRangeDays
        const result = await this.$swal({
          title: this.$t('Attention'),
          html: `
            <div class="text-left">
              <p>${this.$t('The selected period')} (<strong>${days} ${days === 1 ? this.$t('day') : this.$t('days')}</strong>) ${this.$t('exceeds the 7-day search limit.')}</p>
              <p class="mt-2">${this.$t('The export will be performed directly with the selected filters (up to 31 days).')}</p>
              <p class="mt-2"><strong>${this.$t('Do you want to continue?')}</strong></p>
            </div>
          `,
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: this.$t('Yes, export'),
          cancelButtonText: this.$t('Cancel'),
          confirmButtonColor: '#28a745',
          buttonsStyling: true,
          customClass: {
            confirmButton: 'swal-export-confirm-btn mr-1',
            cancelButton: 'swal-export-cancel-btn'
          }
        })
        
        if (!result.isConfirmed) {
          return
        }
      }

      this.isExporting = true
      this.dateRangeWarning = ''

      try {
        // Mostrar feedback de geração
        const loadingSwal = this.$swal({
          title: this.$t('Generating report'),
          text: this.$t('Please wait while we generate your Excel report...'),
          icon: 'info',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading()
          }
        })

        let response
        try {
          response = await store.dispatch('app-operation-time/fetchExportOperationalTimeExcel', {
            search: this.searchQuery,
            ownerId: this.ownerId,
            assetTypeId: this.assetTypeId,
            assetBrandId: this.assetBrandId,
            assetModelId: this.assetModelId,
            assetGroupId: this.assetGroupId,
            shiftStartTime: this.shiftStartTime,
            shiftEndTime: this.shiftEndTime,
            breaks: this.breaks,
            start: this.startDate,
            end: this.endDate,
          })
        } catch (dispatchError) {
          // Fechar loading antes de tratar o erro
          this.$swal.close()
          throw dispatchError
        }

        // Fechar loading
        this.$swal.close()

        // Verificar se a resposta é válida
        if (!response) {
          throw new Error(this.$t('Empty response from server'))
        }

        // Quando axios retorna blob, o blob está em response.data
        // Mas precisamos verificar se response.data existe
        let blobData = response.data
        
        // Se response.data não existir, pode ser que o blob esteja diretamente em response
        if (!blobData && response instanceof Blob) {
          blobData = response
        }
        
        if (!blobData) {
          throw new Error(this.$t('File data not found in server response'))
        }

        // Se o blobData for um Blob que pode conter um erro JSON, verificar
        if (blobData instanceof Blob) {
          // Verificar o tipo MIME para ver se é realmente um Excel ou um erro JSON
          if (blobData.type && blobData.type.includes('json')) {
            // É um erro JSON, ler e parsear
            const text = await blobData.text()
            try {
              const errorData = JSON.parse(text)
              throw new Error(errorData.message || errorData.error || this.$t('Error generating report'))
            } catch (e) {
              throw new Error(text || this.$t('Unknown error generating report'))
            }
          }
        }

        // Criar blob e fazer download
        const blob = blobData instanceof Blob ? blobData : new Blob([blobData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        // Gerar nome do arquivo com data
        const startDateFormatted = this.startDate.split('-').reverse().join('-')
        const endDateFormatted = this.endDate.split('-').reverse().join('-')
        const fileName = `produtividade_${startDateFormatted}_${endDateFormatted}.xlsx`
        
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        // Feedback de sucesso
        this.$swal({
          title: this.$t('Success'),
          text: this.$t('Report exported successfully!'),
          icon: 'success',
          confirmButtonText: this.$t('OK'),
          timer: 3000,
          timerProgressBar: true,
        })

      } catch (error) {
        
        // Garantir que o loading seja fechado
        this.$swal.close()
        
        // Extrair mensagem de erro de diferentes formatos
        let errorMessage = this.$t('An error occurred while exporting the report. Please try again.')
        
        if (error.response) {
          // Erro do axios com resposta
          if (error.response.data) {
            if (typeof error.response.data === 'string') {
              errorMessage = error.response.data
            } else if (error.response.data.message) {
              errorMessage = error.response.data.message
            } else if (error.response.data.error) {
              errorMessage = error.response.data.error
            }
          } else if (error.response.statusText) {
            errorMessage = `${error.response.status}: ${error.response.statusText}`
          }
        } else if (error.message) {
          errorMessage = error.message
        }
        
        // Feedback de erro
        this.$swal({
          title: this.$t('Error'),
          text: errorMessage,
          icon: 'error',
          confirmButtonText: this.$t('OK'),
        })
      } finally {
        this.isExporting = false
      }
    },
  },
  setup() {
    const OPERATION_TIME_APP_STORE_MODULE_NAME = 'app-operation-time'
    const ASSET_APP_STORE_MODULE_NAME = 'app-asset'
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'

    // Options refs
    const manufacturerOptions = ref([{ label: 'Todos', value: '' }])
    const assetsTypeOptions = ref([{ label: 'Todos', value: '' }])
    const modelOptions = ref([{ label: 'Todos', value: '' }])
    const groupOptions = ref([{ label: 'Todos', value: '' }])

    // Register modules
    if (!store.hasModule(OPERATION_TIME_APP_STORE_MODULE_NAME)) {
      store.registerModule(OPERATION_TIME_APP_STORE_MODULE_NAME, operationalTimeStoreModule)
    }
    if (!store.hasModule(ASSET_APP_STORE_MODULE_NAME)) {
      store.registerModule(ASSET_APP_STORE_MODULE_NAME, assetStoreModule)
    }
    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
      store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    }

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(OPERATION_TIME_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(OPERATION_TIME_APP_STORE_MODULE_NAME)
      }
      if (store.hasModule(ASSET_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(ASSET_APP_STORE_MODULE_NAME)
      }
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      }
    })

    const {
      fetchOperationalTime,
    refOperationalTimeTable,
    tableColumns,
    perPage,
    currentPage,
    totalItems,
    dataMeta,
    perPageOptions,
    sortBy,
    sortDesc,
    
    shiftStartTime,
    shiftEndTime,
    breaks,
    startDate,
    endDate,
    showFilters,
    isLoading,
    operationalData,
    
    searchQuery,
    searchOwner,
    ownerId,
    assetTypeId,
    assetBrandId,
    assetModelId,
    assetGroupId,
    
    refetchData,
    clearFilters,
    hasActiveFilters,
    validateDateRange,
    validateDateRangeForSearch,
    validateDateRangeForExport,
    getSelectedDateRangeDays,
    formatTime,
    formatPercentage,
    getEfficiencyVariant,
    updateSearchOwner,
    updateOwnerId,
    getOperationalTimeForDate,
    hasSearched,
    } = useOperationalTimeFleet()

    // Fetch Manufacturers
    store.dispatch('app-asset/fetchAssetsManufacture')
      .then(response => {
        response.data.data.forEach(manufacturer => {
          manufacturerOptions.value.push({
            label: manufacturer.name,
            value: manufacturer.id
          })
        })
      })
      .catch(() => {})

    // Fetch Asset Types
    store.dispatch('app-asset/fetchAssetsType')
      .then(response => {
        response.data.data.forEach(type => {
          assetsTypeOptions.value.push({
            label: type.name,
            value: type.id
          })
        })
      })
      .catch(() => {})

    // Fetch Asset Groups
    store.dispatch('app-management/fetchAllGroup', { q: '', page: 1, perPage: 100 })
      .then(response => {
        response.data.data.forEach(group => {
          groupOptions.value.push({
            label: group.name,
            value: group.id
          })
        })
      })
      .catch(() => {})

    return {
      fetchOperationalTime,
      refOperationalTimeTable,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      sortBy,
      sortDesc,
      
      shiftStartTime,
      shiftEndTime,
      breaks,
      startDate,
      endDate,
      showFilters,
      isLoading,
      operationalData,
      
      searchQuery,
      searchOwner,
      ownerId,
      assetTypeId,
      assetBrandId,
      assetModelId,
      assetGroupId,
      
      manufacturerOptions,
      assetsTypeOptions,
      modelOptions,
      groupOptions,
      
      refetchData,
      clearFilters,
      hasActiveFilters,
      validateDateRange,
      validateDateRangeForSearch,
      validateDateRangeForExport,
      getSelectedDateRangeDays,
      formatTime,
      formatPercentage,
      getEfficiencyVariant,
      updateSearchOwner,
      updateOwnerId,
      getOperationalTimeForDate,
      hasSearched,
    }
  },
  computed: {
    selectedDateRangeDays() {
      return this.getSelectedDateRangeDays()
    },
    isSearchDisabled() {
      const days = this.selectedDateRangeDays
      return days > 7
    },
  },
  mounted() {
    // Não buscar dados automaticamente - aguardar ação do usuário
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.efficiency-legend {
  font-size: 0.857rem;
  gap: 0.25rem;
  
  small {
    font-size: 0.75rem;
  }
  
  .badge {
    padding: 0.25rem 0.5rem;
    font-weight: 500;
  }
}

.badge-notification {
  top: -5px;
  right: -5px;
  font-size: 0.6rem;
  min-width: 16px;
  height: 16px;
  line-height: 14px;
}

.operational-time-cell {
  padding: 0.75rem 0.5rem;
  min-width: 220px;
  line-height: 1.5;
}

.plate-identifier-cell {
  padding: 0.75rem 0.5rem;
  min-width: 200px;
  max-width: 250px;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.5;
  
  div {
    margin-bottom: 0.25rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  small {
    font-size: 0.857rem;
    color: #6e6b7b;
  }
  
  span {
    font-size: 0.857rem;
  }
}

.operational-time-table {
  ::v-deep th:first-child,
  ::v-deep td:first-child {
    position: sticky !important;
    left: 0 !important;
    background-color: #fff !important;
    z-index: 2 !important;
    box-shadow: 2px 0 5px rgba(0, 0, 0, 0.05);
  }
  
  ::v-deep thead th:first-child {
    z-index: 3 !important;
  }
}

.mb-25 {
  margin-bottom: 0.25rem;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.empty-state-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 3rem 2rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  margin: 1rem;
}

.empty-state-content {
  text-align: center;
  max-width: 500px;
}

.empty-state-icon {
  margin-bottom: 1.5rem;
  color: #7367f0;
  opacity: 0.8;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

.empty-state-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 1rem;
}

.empty-state-description {
  font-size: 1rem;
  color: #6e6b7b;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.empty-state-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  background-color: #e7f3ff;
  border-left: 3px solid #7367f0;
  border-radius: 0.25rem;
  color: #5e5873;
  
  small {
    font-size: 0.875rem;
    line-height: 1.5;
  }
  
  svg {
    color: #7367f0;
    flex-shrink: 0;
  }
}

.date-range-info {
  .date-limit-badge {
    padding: 0.5rem 0.75rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    cursor: help;
    
    svg {
      flex-shrink: 0;
    }
    
    small {
      font-size: 0.75rem;
      font-weight: 500;
    }
  }
}

.date-range-badge {
  margin-top: 0.25rem;
  display: inline-flex;
  align-items: center;
  
  svg {
    flex-shrink: 0;
  }
  
  small {
    font-size: 0.75rem;
    font-weight: 500;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

// Estilos para espaçamento dos botões do modal de exportação
::v-deep .swal-export-confirm-btn {
  margin-right: 0.5rem !important;
}

::v-deep .swal-export-cancel-btn {
  margin-left: 0.5rem !important;
}
</style>